import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Primary next-step action (e.g. "+ Add Employee", "Clear Filters", "Retry"). */
  action?: ReactNode;
  /** "error" tints the icon chip red for API/load-failure states, kept in this
   * same component so loading/empty/error all share one visual language. */
  variant?: "default" | "error";
  className?: string;
}

export function EmptyState({ icon, title, description, action, variant = "default", className }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-16 text-center", className)}>
      {icon && (
        <div
          className={cn(
            "mb-1 flex h-14 w-14 items-center justify-center rounded-full",
            variant === "error" ? "bg-red-50 text-red-400" : "bg-primary-50 text-primary-300",
          )}
        >
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {description && <p className="max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/**
 * The "API request failed" counterpart to EmptyState - same layout/rhythm so
 * loading -> error -> empty -> loaded never feels like four different
 * components, just one that failed to fetch rather than one with nothing in
 * it yet. Callers pass their own onRetry (usually a React Query `refetch`).
 */
export function ErrorState({
  title = "Unable to load data",
  description = "Please try again.",
  onRetry,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <EmptyState
      variant="error"
      icon={<AlertIcon />}
      title={title}
      description={description}
      action={
        onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          >
            Retry
          </button>
        )
      }
      className={className}
    />
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7" aria-hidden="true">
      <path d="M10.3 3.86 1.82 18a1 1 0 0 0 .86 1.5h18.64a1 1 0 0 0 .86-1.5L13.7 3.86a1 1 0 0 0-1.73 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}
